<template>
  <v-container fluid pa-0 ma-0 py-5>
    <v-row justify="center">
      <v-col cols="12" class="test_yellow px-0 mx-0">
        <vue-paycard
          :value-fields="valueFields"
          :is-card-number-masked="false"
          :has-random-backgrounds="false"
        />
      </v-col>
      <v-col cols="10" sm="7" md="9" lg="10" class="test_red">
        <v-form ref="Form" v-model="valid" :disabled="$store.getters.isLoading">
          <v-row no-gutters justify="center" class="test_blue">
            <v-col cols="12">
              <v-text-field
                id="v-card-number"
                v-model="valueFields.cardNumber"
                v-mask="'#### #### #### ####'"
                :label="$t('inputs.card_number')"
                type="tel"
                data-card-field
                :rules="[$rules.required, $rules.creditCard]"
                :maxlength="cardNumberMaxLength"
                autocomplete="off"
                @input="changeNumber"
              />
            </v-col>
          </v-row>
          <v-row no-gutters justify="space-between">
            <v-col cols="3">
              <v-select
                :id="inputFields.cardMonth"
                v-model="valueFields.cardMonth"
                label="MM"
                :items="months"
                item-text="name"
                item-value="name"
                :rules="[$rules.required]"
                data-card-field
                @input="changeMonth"
              />
            </v-col>
            <v-col cols="3">
              <v-select
                :id="inputFields.cardYear"
                v-model="valueFields.cardYear"
                label="YY"
                :items="years"
                item-text="name"
                item-value="name"
                data-card-field
                :rules="[$rules.required]"
                @input="changeYear"
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                :id="inputFields.cardCvv"
                v-model="valueFields.cardCvv"
                v-number-only
                label="Cvv"
                type="tel"
                :rules="[$rules.required, $rules.cvv]"
                data-card-field
                autocomplete="off"
                maxlength="4"
                @input="changeCvv"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                :id="inputFields.cardName"
                v-model="valueFields.cardName"
                :label="$t('inputs.card_name')"
                type="tel"
                data-card-field
                autocomplete="off"
                @input="changeName"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { Months, Years } from "../../utils/creditCard";
export default {
  name: "CreditCardComponent",
  directives: {
    "number-only": {
      bind(el) {
        function checkValue(event) {
          event.target.value = event.target.value.replace(/[^0-9]/g, "");
          if (event.charCode >= 48 && event.charCode <= 57) {
            return true;
          }
          event.preventDefault();
        }
        el.addEventListener("keypress", checkValue);
      },
    },
    "letter-only": {
      bind(el) {
        function checkValue(event) {
          if (event.charCode >= 48 && event.charCode <= 57) {
            event.preventDefault();
          }
          return true;
        }
        el.addEventListener("keypress", checkValue);
      },
    },
  },
  data: () => ({
    isLoading: false,
    valid: false,
    months: Months,
    years: Years,
    minCardYear: new Date().getFullYear(),
    mainCardNumber: "",
    cardNumberMaxLength: 19,
    valueFields: {
      cardName: "",
      cardNumber: "",
      cardMonth: "",
      cardYear: "",
      cardCvv: "",
    },
    inputFields: {
      cardNumber: "v-card-number",
      cardName: "v-card-name",
      cardMonth: "v-card-month",
      cardYear: "v-card-year",
      cardCvv: "v-card-cvv",
    },
    options: {
      duration: 1500,
      offset: 0,
      easing: "linear",
    },
  }),
  computed: {
    showError: function () {
      return !this.$store.getters.checkoutValidation.paymentInfo.status;
    },
    minCardMonth() {
      if (this.valueFields.cardYear === this.minCardYear)
        return new Date().getMonth() + 1;
      return 1;
    },
  },
  watch: {
    cardYear() {
      if (this.valueFields.cardMonth < this.minCardMonth) {
        this.valueFields.cardMonth = "";
      }
    },
    valid() {
      if (this.valid) {
        this.$emit("input", this.valueFields);
      } else {
        this.$emit("input", null);
      }
    },
  },
  methods: {
    changeNumber(e) {
      const value = this.valueFields.cardNumber.replace(/\D/g, "");
      // american express, 15 digits
      if (/^3[47]\d{0,13}$/.test(value)) {
        this.valueFields.cardNumber = value
          .replace(/(\d{4})/, "$1 ")
          .replace(/(\d{4}) (\d{6})/, "$1 $2 ");
        this.cardNumberMaxLength = 17;
      } else if (/^3(?:0[0-5]|[68]\d)\d{0,11}$/.test(value)) {
        // diner's club, 14 digits
        this.valueFields.cardNumber = value
          .replace(/(\d{4})/, "$1 ")
          .replace(/(\d{4}) (\d{6})/, "$1 $2 ");
        this.cardNumberMaxLength = 16;
      } else if (/^62[0-9]\d*/.test(value)) {
        this.valueFields.cardNumber = value
          .replace(/(\d{6})/, "$1 ")
          .replace(/(\d{6}) (\d{7})/, "$1 $2 ")
          .replace(/(\d{6}) (\d{7}) (\d{6})/, "$1 $2 $3 ")
          .replace(/(\d{5}) (\d{5}) (\d{5}) (\d{4})/, "$1 $2 $3 $4");
        this.cardNumberMaxLength = 21;
      } else if (/^\d{0,16}$/.test(value)) {
        // regular cc number, 16 digits
        this.valueFields.cardNumber = value
          .replace(/(\d{4})/, "$1 ")
          .replace(/(\d{4}) (\d{4})/, "$1 $2 ")
          .replace(/(\d{4}) (\d{4}) (\d{4})/, "$1 $2 $3 ");
        this.cardNumberMaxLength = 19;
      }
      // eslint-disable-next-line
      if (e.inputType == "deleteContentBackward") {
        const lastChar = this.valueFields.cardNumber.substring(
          this.valueFields.cardNumber.length,
          this.valueFields.cardNumber.length - 1
        );
        // eslint-disable-next-line
        if ((lastChar == " ") | (e == null)) {
          this.valueFields.cardNumber = this.valueFields.cardNumber.substring(
            0,
            this.valueFields.cardNumber.length - 1
          );
        }
      }
      this.$emit("input-card-number", this.valueFields.cardNumber);
    },
    changeMonth() {
      this.$emit("input-card-month", this.valueFields.cardMonth);
    },
    changeYear() {
      this.$emit("input-card-year", this.valueFields.cardYear);
    },
    changeCvv() {
      /* this.valueFields.cardCvv = e.target.value; */
      this.$emit("input-card-cvv", this.valueFields.cardCvv);
    },
    changeName() {
      /* this.valueFields.cardCvv = e.target.value; */
      this.$emit("v-card-name", this.valueFields.cardName);
    },
    generateMonthValue(n) {
      return n < 10 ? `0${n}` : n;
    },
  },
};
</script>
